<template lang="pug">
BriqPanel.scheduler-panel
  h2.mb-20 Scheduler
  ProductSchedulingConfig(
    :product="product"
    :resource="selectedResource"
    :disabled="disabled"
    :vuelidate="vuelidate"
    :enableProductDurationPerParticipant="enableProductDurationPerParticipant"
    @schedulingBehaviorSelected="schedulingBehaviorSelected"
  )
  BriqDurationInput(
    v-if="!hideResetAndApplicableTime"
    label="Reset time"
    name="ResetTimeDuration"
    :infoText="resetTimeInfo"
    help="The time needed to reset the resource after it has come to an end. For example: resetting an escape room after playing"
    v-model="product.ProductDurationConfig.ResetTime"
    :disabled="isResetTimeDisabled"
    :useInputInIsoFormat="true"
    :hideClearButton="true"
    :excludeSeconds="true"
    :error="resetTimeError"
    @blur="vuelidate.product.ResetTime.$touch()"
    data-test-id="reset-time-input"
    )
  BriqDurationInput(
    v-if="!hideResetAndApplicableTime"
    label="Applicable reset time"
    name="ApplicableResetTimeIso"
    v-model="product.ApplicableResetTimeIso"
    :disabled="disabled"
    :useInputInIsoFormat="true"
    :hideClearButton="true"
    :excludeSeconds="true"
    disabled
    )
</template>

<script>
import WebsiteContentFields from '@v/inventory/products/edit/ProductFields/WebsiteContentFields.vue'
import ProductSchedulingConfig from './ProductSchedulingConfig.vue'
import { getHumanReadableTime } from './humanReadableTimeHelper.js'
import BriqDurationInput from '@v/components/BriqDurationInput.vue'
import modelMixin from '@v/mixins/modelMixin'

const ResetTimeType = Object.freeze({
  NotUsed: 'NotUsed',
  CompleteResource: 'CompleteResource',
  PerParticipant: 'PerParticipant',
})

export default {
  name: 'SchedulerPanel',
  components: {
    WebsiteContentFields,
    BriqDurationInput,
    ProductSchedulingConfig,
  },
  mixins: [modelMixin({ type: Object, name: 'product' })],
  props: {
    vuelidate: Object,
    disabled: Boolean,
    enableProductDurationPerParticipant: Boolean,
    selectedResource: Object,
  },
  data() {
    return {
      durationSuggestedValue: '',
      hideResetAndApplicableTime: false,
    }
  },
  computed: {
    resetTimeError() {
      if (!this.vuelidate.product.ProductDurationConfig.ResetTime.$error) return null
      return 'Reset Time is not allowed to be sent in if product duration behavior is Parallel or Non-Scheduled'
    },
    isResetTimeDisabled() {
      return this.disabled || this.selectedResource?.ResetTimeType === ResetTimeType.NotUsed
    },
    resetTimeInfo() {
      let resetTimeTypeMessage = ''
      let defaultResetTimeMessage = ''
      const resourceNameWithLink = `<a href="/inventory/resources/${this.selectedResource?.Key}/edit" target="_blank">${this.selectedResource?.Description}</a>`

      if (this.selectedResource?.ResetTimeType) {
        switch (this.selectedResource.ResetTimeType) {
          case ResetTimeType.NotUsed:
            resetTimeTypeMessage = `Reset time has been disabled for the resource ${resourceNameWithLink} `
            break
          case ResetTimeType.PerParticipant:
            resetTimeTypeMessage = `During the reset time, the used capacity for the resource ${resourceNameWithLink} will be subtracted from the available capacity. `
            break
          case ResetTimeType.CompleteResource:
            resetTimeTypeMessage = `During the reset time, the entire resource ${resourceNameWithLink} will be not bookable. `
            break
        }
      }

      if (
        typeof this.selectedResource?.ResetTime !== 'undefined' &&
        this.selectedResource.ResetTime !== null
      ) {
        const combinedResetTime = getHumanReadableTime(this.selectedResource.ResetTime)
        if (combinedResetTime) {
          defaultResetTimeMessage = `Default reset time from resource is ${combinedResetTime}`
        }
      }
      return `${resetTimeTypeMessage}${defaultResetTimeMessage}`
    },
  },
  methods: {
    toggleResetTimeAndApplicableResetTime(value) {
      if (value === 'noDuration') {
        this.hideResetAndApplicableTime = true
        this.product.ProductDurationConfig.ResetTime = null
      } else {
        this.hideResetAndApplicableTime = false
      }
    },
    schedulingBehaviorSelected(v) {
      this.toggleResetTimeAndApplicableResetTime(v)
      this.$emit('schedulingBehaviorSelected', v)
    },
  },
}
</script>

<style lang="stylus" scoped>
@import '~@v/variables.styl'

h2
  margin-bottom 20px
</style>

<style lang="stylus">
@import '~@v/variables.styl'

.scheduler-panel .briq-form-element
  margin-bottom space-S
</style>
